import { useContext } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Home,
  Calendar,
  FileText,
  Building2,
  ClipboardList,
  MessageSquare,
  PieChart,
  DollarSign,
  FileBarChart,
  Users,
  Bell,
  HelpCircle,
  ExternalLink,
  type LucideIcon
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { PreloadContext } from '../../components/preloader/PreloaderProvider';
import { PATH_HOME, PATH_ADMIN, PATH_PAGE } from '../../routes/paths';
import { brandConfig } from '../../config';
import { guestPortalOrigin } from '../../utils/owner-portal/url/url';
import { useAuthContext } from '../../auth/useAuthContext';
import { adminRoleNames } from '../../assets/data';
import { ScrollArea } from './ScrollArea';
import { useGetRTKQListingsQuery } from '../../redux/rtkQuery/apiSlice';

interface NavItem {
  title: string;
  path: string;
  icon: LucideIcon;
  roles?: string[];
}

interface ExternalLinkItem {
  title: string;
  href: string;
}

interface DashboardNavProps {
  onClose?: () => void;
  /** When true, show only icons (no labels, no section titles). Used when drawer is collapsed on desktop. */
  collapsed?: boolean;
}

export default function DashboardNav({ onClose, collapsed = false }: DashboardNavProps) {
  const location = useLocation();
  const { user } = useAuthContext();
  const { ownerListingIds, hasReports, isTimeshareOwner } = useContext(PreloadContext);
  const { data: listingsData } = useGetRTKQListingsQuery({}, { skip: !!user?.role && adminRoleNames.includes(user.role as any) });

  const isAdmin = user?.role && adminRoleNames.includes(user.role as any);
  const hasListings = ownerListingIds && ownerListingIds.length > 0;

  const guestPortalUrl =
    typeof window !== 'undefined' ? guestPortalOrigin(window.location.hostname) : '';
  const linkItems: ExternalLinkItem[] = [
    ...(guestPortalUrl ? [{ title: 'Guest Portal', href: guestPortalUrl }] : []),
    ...(listingsData ?? [])
      .filter((l) => l.listing_url)
      .map((l) => ({ title: l.name_detailed || l.unit_id || l.name || l.id, href: l.listing_url }))
  ];

  const generalNavItems: NavItem[] = isAdmin
    ? []
    : [
        { title: 'Dashboard', path: PATH_HOME.general.app, icon: Home },
        ...(hasListings
          ? [{ title: 'Properties', path: PATH_HOME.property.root, icon: Building2 }]
          : []),
        { title: 'Bookings', path: PATH_HOME.booking.list, icon: ClipboardList },
        ...(isTimeshareOwner
          ? [{ title: 'Timeshares', path: PATH_HOME.timeshare.root, icon: Calendar }]
          : []),
        { title: 'Statements', path: PATH_HOME.general.statements, icon: FileText },
        { title: 'Calendar', path: PATH_HOME.calendar, icon: Calendar },
        ...(hasListings
          ? [{ title: 'Cases', path: PATH_HOME.case.root, icon: MessageSquare }]
          : [])
      ];

  const reportNavItems: NavItem[] = [];
  if (hasReports) {
    if (brandConfig.reportsEnabled.booking) {
      reportNavItems.push({
        title: 'Booking',
        path: PATH_HOME.reports.bookings,
        icon: FileBarChart
      });
    }
    if (brandConfig.reportsEnabled.occupancy) {
      reportNavItems.push({
        title: 'Occupancy',
        path: PATH_HOME.reports.occupancy,
        icon: PieChart
      });
    }
    if (brandConfig.reportsEnabled.revenue) {
      reportNavItems.push({ title: 'Revenue', path: PATH_HOME.reports.revenue, icon: DollarSign });
    }
  }

  // Admin section: admin routes only (Users; no Notifications, no FAQs)
  const adminNavItems: NavItem[] = isAdmin
    ? [{ title: 'Users', path: PATH_ADMIN.user.list, icon: Users }]
    : [];

  const utilityNavItems: NavItem[] = isAdmin
    ? []
    : [
        { title: 'Notifications', path: PATH_HOME.user.notifications, icon: Bell },
        { title: 'FAQs', path: PATH_PAGE.faqs, icon: HelpCircle }
      ];

  return (
    <ScrollArea className="flex-1">
      <div className="pb-4">
        <NavSection
          title="General"
          items={generalNavItems}
          location={location}
          onClose={onClose}
          collapsed={collapsed}
          showDivider={false}
        />
        {reportNavItems.length > 0 && (
          <NavSection
            title="Reports"
            items={reportNavItems}
            location={location}
            onClose={onClose}
            collapsed={collapsed}
            showDivider
          />
        )}
        {adminNavItems.length > 0 && (
          <NavSection
            title="Admin"
            items={adminNavItems}
            location={location}
            onClose={onClose}
            collapsed={collapsed}
            showDivider
          />
        )}
        {(utilityNavItems.length > 0 || linkItems.length > 0) && (
          <MoreSection
            utilityItems={utilityNavItems}
            linkItems={linkItems}
            location={location}
            onClose={onClose}
            collapsed={collapsed}
          />
        )}
      </div>
    </ScrollArea>
  );
}

function NavSection({
  title,
  items,
  location,
  onClose,
  collapsed = false,
  showDivider = true
}: {
  title?: string;
  items: NavItem[];
  location: { pathname: string };
  onClose?: () => void;
  collapsed?: boolean;
  showDivider?: boolean;
}) {
  if (items.length === 0) return null;

  return (
    <div className={cn('py-2', collapsed && 'px-0')}>
      {title && showDivider && (
        <div className="h-8 flex items-center shrink-0" aria-hidden={collapsed}>
          {collapsed ? (
            <div className="w-full mx-2.5 border-t border-dotted border-border" />
          ) : (
            <h3 className="w-full px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {title}
            </h3>
          )}
        </div>
      )}
      {title && !showDivider && (
        <div className="h-8 flex items-center shrink-0" aria-hidden={collapsed}>
          {collapsed ? (
            <div className="w-full mx-2.5 border-t border-dotted border-border" />
          ) : (
            <h3 className="w-full px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {title}
            </h3>
          )}
        </div>
      )}
      <nav className={cn('flex flex-col gap-0.5', collapsed ? 'px-2.5' : 'px-2')}>
        {items.map((item) => {
          const isActive =
            location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          const Icon = item.icon;

          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              title={collapsed ? item.title : undefined}
              className={cn(
                'flex h-10 shrink-0 items-center rounded-lg text-sm font-medium transition-colors',
                collapsed ? 'justify-center px-0 min-w-0' : 'gap-3 px-3 min-w-0',
                isActive
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {!collapsed && (
                <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                  {item.title}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

function MoreSection({
  utilityItems,
  linkItems,
  location,
  onClose,
  collapsed = false
}: {
  utilityItems: NavItem[];
  linkItems: ExternalLinkItem[];
  location: { pathname: string };
  onClose?: () => void;
  collapsed?: boolean;
}) {
  return (
    <div className={cn('py-2', collapsed && 'px-0')}>
      <div className="h-8 flex items-center shrink-0" aria-hidden={collapsed}>
        {collapsed ? (
          <div className="w-full mx-2.5 border-t border-dotted border-border" />
        ) : (
          <h3 className="w-full px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            More
          </h3>
        )}
      </div>
      <nav className={cn('flex flex-col gap-0.5', collapsed ? 'px-2.5' : 'px-2')}>
        {utilityItems.map((item) => {
          const isActive =
            location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onClose}
              title={collapsed ? item.title : undefined}
              className={cn(
                'flex h-10 shrink-0 items-center rounded-lg text-sm font-medium transition-colors',
                collapsed ? 'justify-center px-0 min-w-0' : 'gap-3 px-3 min-w-0',
                isActive
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {!collapsed && (
                <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                  {item.title}
                </span>
              )}
            </NavLink>
          );
        })}
        {linkItems.map((item) => (
          <a
            key={item.href + item.title}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            title={collapsed ? item.title : undefined}
            className={cn(
              'flex h-10 shrink-0 items-center rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:bg-muted/70 hover:text-foreground',
              collapsed ? 'justify-center px-0 min-w-0' : 'gap-3 px-3 min-w-0'
            )}
          >
            <ExternalLink className="h-4 w-4 shrink-0" />
            {!collapsed && (
              <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                {item.title}
              </span>
            )}
          </a>
        ))}
      </nav>
    </div>
  );
}
